import "katex/dist/katex.min.css"

import { FC, isValidElement, ReactElement, useMemo } from "react"
import { getChildDeep } from "react-nanny"
import { Box, styled } from "@mui/material"
import { renderToStaticMarkup } from "react-dom/server"
import { MathComponent } from "mathjax-react"
import { renderKatexAccessible } from "./renderKatexAccessible"
import { useMathSpeakableContext } from "../context/MathSpeakableContext"
import parse from "html-react-parser"

interface MathMLProps {
  itemId: string
  children: JSX.Element
}

enum Encodings {
  katex = "application/x-tex"
}

export const MathMLComponent: FC<MathMLProps> = ({ itemId, children }) => {
  const { onSpeakableText } = useMathSpeakableContext()

  const katexFormula = useMemo(() => {
    const annotation = getChildDeep(children, child => {
      if (!isValidElement(child)) return false
      return (child as ReactElement).type === "annotation"
    }) as ReactElement | undefined

    if (!annotation || annotation?.props?.encoding !== Encodings.katex)
      return ""

    if (
      Array.isArray(annotation.props?.children) &&
      annotation.props.children.length === 1
    ) {
      return annotation.props.children[0] as string
    }

    if (typeof annotation.props?.children !== "string") return ""

    return annotation.props.children as string
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId])

  const katexResult = useMemo(
    () =>
      katexFormula
        ? renderKatexAccessible(katexFormula, { displayMode: false })
        : null,
    [katexFormula]
  )

  if (!katexFormula || !katexResult) {
    return (
      <MathMLContainer>
        <MathComponent
          mathml={`<math>${renderToStaticMarkup(children)}<math/>`}
        />
      </MathMLContainer>
    )
  }

  const { html, speakable, isPlainNumber } = katexResult

  if (isPlainNumber) {
    return <span>{speakable}</span>
  }

  const inChoice = onSpeakableText !== null
  return (
    <KatexContainer
      role={speakable ? "img" : "math"}
      aria-label={speakable || undefined}
      {...(!inChoice &&
        speakable && {
          sx: { display: "inline-block" }
        })}
    >
      {!inChoice && speakable ? (
        <Box component="span" aria-hidden="true">
          {parse(html)}
        </Box>
      ) : (
        parse(html)
      )}
    </KatexContainer>
  )
}

const KatexContainer = styled(Box)(({ theme: { spacing } }) => ({
  display: "inline",
  "& .katex:has(.frac), & .katex:has(.array), & .katex:has(.mfrac)": {
    "& .base": {
      marginBottom: spacing(2),
      marginTop: "2px"
    }
  }
}))

const MathMLContainer = styled(Box)(() => ({
  display: "inline",
  "& div": {
    display: "inherit"
  }
}))
